import { $, $$, el } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { pageData } from '../lib/dom.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { toastSuccess, toastError, confirmAction } from '../lib/toast.js';
import { findSelectMenu } from '../lib/select-menu.js';
import {
  CARD_BACKGROUND_WARNINGS,
  mountCardPreview,
  readAppearance,
} from '../lib/card-preview.js';
import { totalXpForLevel } from '../lib/level-curve.js';

interface Reward {
  level: number;
  roleId: string;
}

interface RoleOption {
  id: string;
  name: string;
  hexColor: string | null;
  aboveBot?: boolean;
}

interface LevelsData {
  settings: { rewards: Reward[] };
  roles: RoleOption[];
  guildId: string;
}

export function initLevelsPage(): void {
  const found = $<HTMLFormElement>('[data-levels-form]');
  if (!found) return;

  // Captured into a non-null constant: the narrowing from `if (!found)` does
  // not reach the functions declared below, which are hoisted.
  const form: HTMLFormElement = found;
  const guildId = form.dataset.guildId ?? '';

  /*
   * Saving is armed **first**, before reading the page data and before the
   * reward rows or the card preview are built. All of them can throw on
   * unexpected data, and saving is the essential function of the page: it must
   * depend on no widget at all.
   */
  const rows = $('[data-reward-rows]', form);
  const empty = $('[data-reward-empty]', form);

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      const answer = await api.put<{ warnings?: string[] }>(`/api/levels?guildId=${guildId}`, {
        guildId,
        minXp: number(form, 'minXp', 15),
        maxXp: number(form, 'maxXp', 25),
        xpRate: Number(radio(form, 'xpRate', '1')),
        cooldownSeconds: number(form, 'cooldownSeconds', 60),

        announceMode: radio(form, 'announceMode', 'channel'),
        announceChannelId: findSelectMenu(form, 'announceChannelId')?.single ?? '',
        announceMessage: text(form, 'announceMessage'),

        rewardMode: radio(form, 'rewardMode', 'stack'),
        // The rows live outside the form: they are rebuilt from the DOM, like
        // the reaction editor does.
        rewards: readRewards(),
        removeRolesOnDemotion: checkbox(form, 'removeRolesOnDemotion', false),
        resetOnLeave: checkbox(form, 'resetOnLeave', false),

        channelFilterMode: radio(form, 'channelFilterMode', 'exclude'),
        filteredChannelIds: findSelectMenu(form, 'filteredChannelIds')?.value ?? [],
        roleFilterMode: radio(form, 'roleFilterMode', 'exclude'),
        filteredRoleIds: findSelectMenu(form, 'filteredRoleIds')?.value ?? [],

        voice: {
          enabled: checkbox(form, 'voiceEnabled', false),
          mode: radio(form, 'voiceMode', 'merged'),
          minXpPerMinute: number(form, 'voiceMin', 8),
          maxXpPerMinute: number(form, 'voiceMax', 12),
        },

        card: readAppearance(form, 'levels'),
        publicLeaderboard: checkbox(form, 'publicLeaderboard', true),
      });

      // Saved either way: a background the server cannot download is said,
      // not refused, so the rest of the page is never held hostage by it.
      for (const warning of answer.warnings ?? []) {
        toastError(ct(warning, CARD_BACKGROUND_WARNINGS[warning] ?? warning));
      }
    },
  });

  const data = pageData<LevelsData>();
  const roles = data?.roles ?? [];

  /** Rebuilds the reward list from the rows currently on screen. */
  function readRewards(): Reward[] {
    return $$<HTMLElement>('[data-reward-row]', form)
      .map((row) => ({
        level: Number(row.querySelector<HTMLInputElement>('[data-reward-level]')?.value ?? 0),
        roleId: row.querySelector<HTMLSelectElement>('[data-reward-role]')?.value ?? '',
      }))
      .filter((reward) => reward.level > 0 && reward.roleId !== '');
  }

  function renderEmptyState(): void {
    empty?.classList.toggle('hidden', $$('[data-reward-row]', form).length > 0);
  }

  /** One reward row: a level, a role, and a way to remove it. */
  function addRow(reward: Reward = { level: 1, roleId: '' }): void {
    if (!rows) return;

    const row = el('div', {
      class: 'flex flex-wrap items-end gap-2',
      attrs: { 'data-reward-row': '' },
    });

    const levelField = el('input', {
      class: 'input input-bordered w-24',
      attrs: {
        type: 'number',
        min: '1',
        max: '1000',
        'data-reward-level': '',
        'aria-label': ct('rewardLevel', 'Level'),
      },
    });
    levelField.value = String(reward.level);

    /*
     * The XP that level takes, beside it.
     *
     * "Tier 3" meant nothing to a customer until he found MEE6's table: the
     * number is a level, and a level only means something as XP (28 Sep 2026).
     */
    const xpHint = el('span', {
      class: 'field-hint self-center whitespace-nowrap',
      attrs: { 'data-reward-xp': '' },
    });
    const showXp = () => {
      const level = Math.max(0, Math.floor(Number(levelField.value) || 0));
      xpHint.textContent = ct('rewardXp', '{xp} XP', {
        xp: new Intl.NumberFormat(document.documentElement.lang || undefined).format(
          totalXpForLevel(level),
        ),
      });
    };
    showXp();
    levelField.addEventListener('input', showXp);

    const rolePicker = el('select', {
      class: 'select select-bordered min-w-48 flex-1',
      attrs: { 'data-reward-role': '', 'aria-label': ct('rewardRole', 'Role') },
    });
    rolePicker.append(
      el('option', { text: ct('chooseRole', '- Pick a role -'), attrs: { value: '' } }),
    );

    for (const role of roles) {
      const option = el('option', {
        text: role.aboveBot
          ? `@${role.name} · ${ct('outOfReach', 'out of reach')}`
          : `@${role.name}`,
        attrs: { value: role.id },
      });
      // A role above the bot cannot be granted: offering it would promise a
      // reward the bot is unable to hand out.
      if (role.aboveBot) option.disabled = true;
      rolePicker.append(option);
    }
    rolePicker.value = reward.roleId;

    const remove = el('button', {
      class: 'btn btn-ghost btn-sm',
      text: '✕',
      attrs: {
        type: 'button',
        'aria-label': ct('removeItem', 'Remove {name}', { name: '' }).trim(),
      },
    });
    remove.addEventListener('click', () => {
      row.remove();
      renderEmptyState();
      form.dispatchEvent(new Event('change', { bubbles: true }));
    });

    row.append(levelField, xpHint, rolePicker, remove);
    rows.append(row);
    renderEmptyState();
  }

  for (const reward of data?.settings.rewards ?? []) addRow(reward);
  renderEmptyState();

  $('[data-reward-add]', form)?.addEventListener('click', () => {
    addRow();
    form.dispatchEvent(new Event('change', { bubbles: true }));
  });

  mountCardPreview({
    form,
    name: 'levels',
    endpoint: `/api/levels/card-preview?guildId=${guildId}`,
    read: () => ({ guildId, card: readAppearance(form, 'levels') }),
  });

  mountImport(form, guildId);

  /*
   * Wiping the leaderboard is irreversible, and the button sits on a page whose
   * other control is an ordinary save: it asks first.
   */
  $('[data-levels-reset]', form)?.addEventListener('click', () => {
    void (async () => {
      const confirmed = await confirmAction(
        ct('confirmResetLeaderboard', 'Reset the leaderboard? Every member loses their XP.'),
      );
      if (!confirmed) return;

      await api.delete(`/api/levels/xp?guildId=${guildId}`);
      toastSuccess(ct('leaderboardReset', 'Leaderboard reset.'));
      window.location.reload();
    })();
  });
}

/**
 * Importing from another bot.
 *
 * Asks first, and says what it destroys. This is the only control on the page
 * that throws away data the server cannot get back.
 */
function mountImport(form: HTMLFormElement, guildId: string): void {
  const button = $<HTMLButtonElement>('[data-import-run]', form);
  if (!button) return;

  button.addEventListener('click', () => {
    void (async () => {
      const confirmed = await confirmAction(
        ct('confirmImportLevels', 'Replace every level with the imported data?'),
      );
      if (!confirmed) return;

      button.disabled = true;
      try {
        const result = await api.post<{ result: { imported: number } }>(
          `/api/levels/import?guildId=${guildId}`,
          { guildId, provider: radio(form, 'importProvider', 'mee6') },
        );

        toastSuccess(
          ct('levelsImported', '{count} members imported.', {
            count: String(result.result.imported),
          }),
        );
        window.location.reload();
      } catch (err) {
        toastError(err instanceof Error ? err.message : ct('importFailed', 'Import failed.'));
      } finally {
        button.disabled = false;
      }
    })();
  });
}

/**
 * Value of a radio group.
 *
 * The mode choices are radios rather than dropdowns: which one is selected has
 * to be readable without opening anything, which is the whole point of the
 * option cards. `form.elements` returns a `RadioNodeList` for a group, whose
 * `.value` is already the checked one.
 */
function radio(form: HTMLFormElement, name: string, fallback: string): string {
  const field = form.elements.namedItem(name);
  if (field instanceof RadioNodeList) return field.value || fallback;
  return field instanceof HTMLInputElement ? field.value || fallback : fallback;
}

function number(form: HTMLFormElement, name: string, fallback: number): number {
  const value = Number(
    form.elements.namedItem(name) instanceof HTMLInputElement
      ? (form.elements.namedItem(name) as HTMLInputElement).value
      : fallback,
  );
  return Number.isFinite(value) ? value : fallback;
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLTextAreaElement || field instanceof HTMLInputElement
    ? field.value
    : '';
}

function checkbox(form: HTMLFormElement, name: string, fallback: boolean): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : fallback;
}
